import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccountValidation } from '@/hooks/useAccountValidation';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText, Mail, CheckCircle, TrendingUp, Target, BarChart3, Briefcase, ArrowRight } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Link } from "react-router-dom";
import { useCredits } from "@/hooks/useCredits";
import { Logo } from "@/components/Logo";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Helmet } from "react-helmet-async";
import { UpgradeBanner } from "@/components/UpgradeBanner";

interface ResumeBreakdown {
  contactInfo: number;
  workExperience: number;
  education: number;
  skills: number;
  overall: number;
}

interface JobDescBreakdown {
  matchedKeywords: number;
  requiredSkills: number;
  experienceLevel: number;
  overallMatch: number;
}

interface DashboardStats {
  totalResumes: number;
  totalLetters: number;
  totalApplications: number;
  averageAtsScore: number;
  recentAtsScores: Array<{ date: string; score: number }>;
  applicationsByStatus: Record<string, number>;
  creditsRemaining: number;
  creditsUsed: number;
  hasCompletedProfile: boolean;
  resumeBreakdown: ResumeBreakdown;
  jobDescBreakdown: JobDescBreakdown;
  monthlyActivity: Array<{ month: number; resumes: number; letters: number }>;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accountStatus, loading: validationLoading } = useAccountValidation();
  const { subscription } = useSubscription();
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const { credits: creditsData, loading: creditsLoading, refetch: refetchCredits } = useCredits();
  const credits = creditsData || { remaining: 0, quota: 0, used: 0, plan: 'free' as const };

  useEffect(() => {
    checkUser();
    refetchCredits();
    
    if (searchParams.get("paid") === "1") {
      toast.success("Payment received — credits added to your account!", {
        duration: 5000,
      });
      refetchCredits();
      setSearchParams({});
    }
    
    // Account validation effect
    if (accountStatus && !validated) {
      console.log('Account status validated:', accountStatus);
      setValidated(true);
      
      if (accountStatus.subscription === 'expired') {
        console.warn('Subscription expired');
      }
    }
  }, [searchParams, accountStatus, validated]);

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    }
  }, [user]);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/login");
        return;
      }

      setUser(user);
    } catch (error: any) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const userId = user?.id;
      if (!userId) return;

      // Check if profile is completed
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, headline, email, phone, linkedin_url')
        .eq('id', userId)
        .single();

      const hasCompletedProfile = !!(profile?.full_name && profile?.headline);

      // Calculate resume breakdown
      const contactInfoScore = profile ? 
        (profile.full_name ? 20 : 0) + 
        (profile.email ? 20 : 0) + 
        (profile.phone ? 20 : 0) + 
        (profile.linkedin_url ? 20 : 0) : 0;

      // Fetch work experience
      const { data: workExps } = await supabase
        .from('work_experiences')
        .select('*')
        .eq('user_id', userId);

      const workExpScore = workExps && workExps.length > 0 ? 
        Math.min(100, workExps.length * 25) : 0;

      // Fetch education
      const { data: educations } = await supabase
        .from('educations')
        .select('*')
        .eq('user_id', userId);

      const educationScore = educations && educations.length > 0 ? 
        Math.min(100, educations.length * 33) : 0;

      // Fetch skills
      const { data: skillsData } = await supabase
        .from('skills')
        .select('*')
        .eq('user_id', userId);

      const skillsScore = skillsData && skillsData.length > 0 ? 
        Math.min(100, skillsData.length * 10) : 0;

      const overallResumeScore = Math.round(
        (contactInfoScore + workExpScore + educationScore + skillsScore) / 4
      );

      // Fetch job analyses for breakdown
      const { data: jobAnalyses } = await supabase
        .from('job_analyses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      const latestJobAnalysis = jobAnalyses?.[0];
      const jobDescBreakdown = {
        matchedKeywords: latestJobAnalysis ? 
          Math.min(100, (latestJobAnalysis.keywords as any[])?.length * 10 || 0) : 0,
        requiredSkills: latestJobAnalysis ? 
          Math.min(100, (latestJobAnalysis.required_skills as any[])?.length * 15 || 0) : 0,
        experienceLevel: latestJobAnalysis?.experience_level ? 75 : 0,
        overallMatch: latestJobAnalysis ? 65 : 0,
      };

      // Fetch resume count
      const { count: resumeCount } = await supabase
        .from('resume_versions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Fetch letter count
      const { count: letterCount } = await supabase
        .from('letters')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Fetch job applications count and status breakdown
      const { data: applications } = await supabase
        .from('job_applications')
        .select('status')
        .eq('user_id', userId);

      const applicationsByStatus = applications?.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Fetch ATS scores
      const { data: atsScores } = await supabase
        .from('ats_scores')
        .select('overall_score, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(12);

      const averageAtsScore = atsScores && atsScores.length > 0
        ? Math.round(atsScores.reduce((sum, score) => sum + score.overall_score, 0) / atsScores.length)
        : 0;

      const recentAtsScores = atsScores?.map(score => ({
        date: new Date(score.created_at).toLocaleDateString(),
        score: score.overall_score
      })) || [];

      // Calculate monthly activity (last 12 months)
      const now = new Date();
      const monthlyActivity = await Promise.all(
        Array.from({ length: 12 }, async (_, i) => {
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);

          const { count: resumeCount } = await supabase
            .from('resume_versions')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .gte('created_at', monthStart.toISOString())
            .lte('created_at', monthEnd.toISOString());

          const { count: letterCount } = await supabase
            .from('letters')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .gte('created_at', monthStart.toISOString())
            .lte('created_at', monthEnd.toISOString());

          return {
            month: i + 1,
            resumes: resumeCount || 0,
            letters: letterCount || 0,
          };
        })
      );

      setStats({
        totalResumes: resumeCount || 0,
        totalLetters: letterCount || 0,
        totalApplications: applications?.length || 0,
        averageAtsScore,
        recentAtsScores,
        applicationsByStatus,
        creditsRemaining: credits.remaining,
        creditsUsed: credits.used,
        hasCompletedProfile,
        resumeBreakdown: {
          contactInfo: contactInfoScore,
          workExperience: workExpScore,
          education: educationScore,
          skills: skillsScore,
          overall: overallResumeScore,
        },
        jobDescBreakdown,
        monthlyActivity: monthlyActivity.reverse(),
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (loading || creditsLoading || validationLoading || statsLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div>
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-10 w-36" />
          </div>
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-64 w-full mb-4" />
          <div className="grid gap-6 md:grid-cols-3">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      saved: 'bg-blue-500/20 text-blue-400',
      applied: 'bg-yellow-500/20 text-yellow-400',
      interviewing: 'bg-purple-500/20 text-purple-400',
      offered: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'from-green-500 to-emerald-600';
    if (score >= 75) return 'from-yellow-500 to-orange-500';
    if (score >= 60) return 'from-orange-500 to-red-500';
    return 'from-red-500 to-red-700';
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | LoopBaan.ai - Your AI Career Tools</title>
        <meta name="description" content="Access your AI-powered resume builder, cover letter generator, and premium career optimization tools." />
      </Helmet>
      
      <DashboardLayout>
        <div className="p-6 space-y-6">
          {/* Top Bar */}
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 ring-2 ring-primary/20">
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-primary-foreground text-lg font-bold">
                  {user?.email ? getInitials(user.email) : "U"}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {user?.email?.split('@')[0] || 'User'}
                </h1>
                <p className="text-sm text-muted-foreground">Career Dashboard</p>
              </div>
            </div>
            <Link to="/app/resume">
              <Button className="px-6 py-5 text-base font-semibold">
                NEW DOCUMENT
              </Button>
            </Link>
          </div>

          {/* Upgrade Banner - Show for Free & Pro users */}
          <div className="mb-8">
            <UpgradeBanner />
          </div>

          {/* Quick Start Guide */}
          {!stats.hasCompletedProfile && (
            <Card className="mb-8 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Target className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Complete Your Profile</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Get started by completing your professional profile to unlock AI-powered resume and cover letter generation.
                    </p>
                    <Link to="/app/onboarding">
                      <Button>
                        Complete Profile <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How It Works */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-foreground mb-6">Get Started in 3 Easy Steps</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 1 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary border-2 border-primary/20">
                    1
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Build Your Profile</h3>
                  <p className="text-muted-foreground text-sm">
                    Complete your professional profile with work history, education, and skills
                  </p>
                  <Link to="/app/onboarding">
                    <Button variant="outline">
                      {stats.hasCompletedProfile ? 'Update Profile' : 'Start Profile'}
                    </Button>
                  </Link>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary border-2 border-primary/20">
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Generate Documents</h3>
                  <p className="text-muted-foreground text-sm">
                    Create AI-powered resumes and cover letters tailored to each job
                  </p>
                  <Link to="/app/resume">
                    <Button variant="outline">
                      Create Resume
                    </Button>
                  </Link>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary border-2 border-primary/20">
                    3
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Track & Optimize</h3>
                  <p className="text-muted-foreground text-sm">
                    Check your ATS score and track applications to improve your success rate
                  </p>
                  <Link to="/app/ats-score-checker">
                    <Button variant="outline">
                      Check ATS Score
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">Career Overview</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Left Panel - Resume Breakdown */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Resume</h3>
                  <Badge variant="secondary">
                    {stats.resumeBreakdown.overall}%
                  </Badge>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.resumeBreakdown.contactInfo >= 80 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.resumeBreakdown.contactInfo > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Contact Info</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.resumeBreakdown.contactInfo}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.resumeBreakdown.workExperience >= 75 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.resumeBreakdown.workExperience > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Work Experience</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.resumeBreakdown.workExperience}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.resumeBreakdown.education >= 66 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.resumeBreakdown.education > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Education</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.resumeBreakdown.education}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.resumeBreakdown.skills >= 50 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.resumeBreakdown.skills > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Skills</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.resumeBreakdown.skills}%
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Basis Stats</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Resumes</span>
                      <span className="text-primary font-semibold">{stats.totalResumes}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Key Versions</span>
                      <span className="text-muted-foreground">{Math.min(stats.totalResumes, 3)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Credits Remaining</span>
                      <span className="text-primary font-semibold">{stats.creditsRemaining}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Middle Panel - ATS Score Chart */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">ATS Score Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.recentAtsScores.slice(-8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Average Score</span>
                    <span className="text-primary font-bold text-xl">{stats.averageAtsScore}%</span>
                  </div>
                </div>
              </Card>

              {/* Right Panel - Job Description Match */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Job Match</h3>
                  <Badge variant="secondary">
                    {stats.jobDescBreakdown.overallMatch}%
                  </Badge>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.jobDescBreakdown.matchedKeywords >= 70 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.jobDescBreakdown.matchedKeywords > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Keyword Match</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.jobDescBreakdown.matchedKeywords}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.jobDescBreakdown.requiredSkills >= 70 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.jobDescBreakdown.requiredSkills > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Required Skills</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.jobDescBreakdown.requiredSkills}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {stats.jobDescBreakdown.experienceLevel >= 70 ? (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      ) : stats.jobDescBreakdown.experienceLevel > 0 ? (
                        <Target className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm text-muted-foreground">Experience Level</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {stats.jobDescBreakdown.experienceLevel}%
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Sort Field</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Cover Letters</span>
                      <span className="text-primary font-semibold">{stats.totalLetters}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Applications</span>
                      <span className="text-muted-foreground">{stats.totalApplications}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Success Rate</span>
                      <span className="text-primary font-semibold">
                        {stats.totalApplications > 0 
                          ? Math.round((stats.applicationsByStatus['offered'] || 0) / stats.totalApplications * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

            </div>

            {/* Analytics Cards - Activity Analytics */}
            <h2 className="text-2xl font-bold text-foreground mb-6 mt-8">Activity Analytics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Monthly Document Creation */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">Document Trends</h3>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.monthlyActivity.slice(-8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="resumes" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="letters" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-primary"></div>
                    <span>Resumes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-accent"></div>
                    <span>Letters</span>
                  </div>
                </div>
              </Card>

              {/* Application Status Breakdown */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">Applications</h3>
                  </div>
                </div>
                {Object.keys(stats.applicationsByStatus).length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={Object.entries(stats.applicationsByStatus).map(([status, count]) => ({
                            name: status,
                            value: count
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="hsl(var(--primary))"
                          dataKey="value"
                        >
                          {Object.entries(stats.applicationsByStatus).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`hsl(var(--${index % 2 === 0 ? 'primary' : 'accent'}))`} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 space-y-1">
                      {Object.entries(stats.applicationsByStatus).map(([status, count], i) => (
                        <div key={status} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground capitalize">{status}</span>
                          <span className="font-semibold text-foreground">{count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[200px] flex items-center justify-center">
                    <p className="text-muted-foreground text-sm">No applications yet</p>
                  </div>
                )}
              </Card>

              {/* ATS Score Distribution */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-accent/20">
                      <Briefcase className="h-5 w-5 text-accent" />
                    </div>
                    <span className="text-sm text-white/70">Applications</span>
                  </div>
                  <Badge className="bg-accent/20 text-accent border-accent/30">
                    Status
                  </Badge>
                </div>
                <h3 className="text-lg font-bold text-white mb-4">Application Status</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle className="h-3 w-3 text-accent" />
                    <span>Job Distribution</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle className="h-3 w-3 text-accent" />
                    <span>Status Tracking</span>
                  </div>
                </div>
                {Object.keys(stats.applicationsByStatus).length > 0 ? (
                  <>
                    <div className="h-32 flex items-end justify-between gap-1">
                      {Object.entries(stats.applicationsByStatus).map(([status, count], i) => (
                        <div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-accent to-accent/40 rounded-t relative group"
                          style={{ height: `${Math.min(100, count * 25)}%` }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white bg-black/50 px-2 py-1 rounded whitespace-nowrap capitalize">
                            {status}: {count}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4 text-xs text-white/40">
                      <span>Tracked</span>
                      <span>ATS Optimized</span>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-3 w-3" />
                        <span>{stats.totalApplications} apps</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-32 flex items-center justify-center">
                    <p className="text-white/50 text-sm">No applications yet</p>
                  </div>
                )}
              </Card>

              {/* ATS Score Comparison */}
              <Card className="bg-gradient-to-br from-[#0f4d5f] to-[#0d3a4f] border-accent/20 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">Score Progress</h3>
                  </div>
                </div>
                {stats.recentAtsScores.length > 0 ? (
                  <>
                    <div className="flex items-center justify-center mb-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-primary">{stats.averageAtsScore}%</div>
                        <div className="text-xs text-muted-foreground">Average ATS Score</div>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={stats.recentAtsScores.slice(-6)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={8} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={8} domain={[0, 100]} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="score" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <div className="h-[200px] flex items-center justify-center">
                    <p className="text-muted-foreground text-sm">No ATS scores yet</p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default Dashboard;